import {
    AspectRatio,
    Box,
    CldImage,
    CldVideo,
    CldVideoPlayerProvider,
    Flex,
    hasCloudinaryAsset,
    isCloudinaryVideo,
} from "@project/ui";
import { frostedGlassStyle } from "@porsche-design-system/components-react/styles";
import type {
    DriverFieldsFragment,
    PageArticleFieldsFragment,
} from "@/lib/contentful/__generated/graphql.types";
import type { ReactNode } from "react";
import { WrapperContainer } from "../wrapper-container";
import { TemporaryPlayPauseButton } from "../temporary-play-pause-button";
import { AnimatingWrapper } from "@/components/animating-wrapper";
import { useAppStore } from "@/store/app-store";

type ModuleTypes = DriverFieldsFragment | PageArticleFieldsFragment;

type HeroSplitScreenProps = ModuleTypes & {
    primaryComponent?: ReactNode;
    secondaryComponent?: ReactNode;
};

export const HeroSplitScreen = (props: HeroSplitScreenProps) => {
    const { primaryComponent, secondaryComponent } = props;

    const {
        state: { hasLiveTicker },
    } = useAppStore();

    const isDriverPage = props.__typename === "Driver";
    const asset = isDriverPage ? props.asset : props.heroAsset;

    return (
        <CldVideoPlayerProvider autoplay muted loop>
            <Flex h="auto" overflow="hidden" flexDirection={{ base: "column-reverse", l: "row" }}>
                {hasCloudinaryAsset(asset) && (
                    <>
                        <Box position="relative">
                            <Box
                                position="relative"
                                w={{ base: "100vw", l: "50vw" }}
                                h={{
                                    base: "auto",
                                    l: hasLiveTicker ? "screenHeightWithLiveTicker" : "100svh",
                                }}
                                pt={{ base: "navHeightMobile", s: "navHeightDesktop" }}
                            >
                                {isCloudinaryVideo(asset) ? (
                                    <CldVideo
                                        cloudinaryAsset={asset}
                                        wrapperProps={{
                                            position: "absolute",
                                            borderRadius: "unset",
                                        }}
                                        loop
                                    />
                                ) : (
                                    <CldImage
                                        cloudinaryAsset={asset}
                                        priority={true}
                                        fill
                                        wrapperProps={{
                                            position: "absolute",
                                        }}
                                        position="absolute"
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                        }}
                                    />
                                )}
                                <Box
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    width="100%"
                                    height="100%"
                                    minHeight="100%"
                                    sx={{
                                        ...frostedGlassStyle,
                                    }}
                                    zIndex={0}
                                />
                                <Box
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    width="100%"
                                    height="100%"
                                    minHeight="100%"
                                    bgColor="porscheBlack"
                                    opacity={0.6}
                                    bgGradient="radial(transparent, porscheBlack)"
                                    zIndex={1}
                                />
                                <Box
                                    position={{ base: "relative", l: "absolute" }}
                                    width="100%"
                                    bottom="0"
                                    left="0"
                                    zIndex={2}
                                >
                                    <Box display={{ base: "block", l: "none" }}>
                                        <AnimatingWrapper>
                                            <WrapperContainer isSplitLayout={true}>
                                                {primaryComponent}
                                            </WrapperContainer>
                                        </AnimatingWrapper>
                                        <AspectRatio ratio={"1:1"}>
                                            {isCloudinaryVideo(asset) ? (
                                                <CldVideo
                                                    cloudinaryAsset={asset}
                                                    wrapperProps={{
                                                        position: "absolute",
                                                        borderRadius: "unset",
                                                    }}
                                                    loop
                                                />
                                            ) : (
                                                <CldImage
                                                    cloudinaryAsset={asset}
                                                    priority={true}
                                                    fill
                                                    wrapperProps={{ borderRadius: "unset" }}
                                                />
                                            )}
                                        </AspectRatio>
                                        {hasCloudinaryAsset(asset) && isCloudinaryVideo(asset) && (
                                            <TemporaryPlayPauseButton />
                                        )}
                                    </Box>
                                    <WrapperContainer isSplitLayout={true}>
                                        <AnimatingWrapper delay={0.8}>
                                            <Box display={{ base: "none", l: "block" }}>
                                                {primaryComponent}
                                            </Box>
                                        </AnimatingWrapper>
                                        {secondaryComponent}
                                    </WrapperContainer>
                                </Box>
                            </Box>
                        </Box>
                        <Box display={{ base: "none", l: "block" }} pos="relative">
                            <Box
                                width={"50vw"}
                                h={hasLiveTicker ? "screenHeightWithLiveTicker" : "100svh"}
                            >
                                {isCloudinaryVideo(asset) ? (
                                    <CldVideo
                                        cloudinaryAsset={asset}
                                        wrapperProps={{
                                            position: "absolute",
                                            borderRadius: "unset",
                                        }}
                                        loop
                                    />
                                ) : (
                                    <CldImage
                                        cloudinaryAsset={asset}
                                        priority={true}
                                        fill
                                        wrapperProps={{ borderRadius: "unset" }}
                                    />
                                )}
                                {hasCloudinaryAsset(asset) && isCloudinaryVideo(asset) && (
                                    <AnimatingWrapper>
                                        <TemporaryPlayPauseButton />
                                    </AnimatingWrapper>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </Flex>
        </CldVideoPlayerProvider>
    );
};
